import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { ShowMore, ShowLess } from "../icons"
import { AnimatedNumber, Collapse } from "../motion";
import { getTypographyClass } from "../../utils/typography";
import {
    CHILD_SEAT_PRICE,
    getBookingPriceBreakdown,
    getDiscountLabelKeys,
    isDiscountApplied,
} from "../../utils/discount";

const TotalCard = ({
    cart,
    lang,
    className = "d-block d-md-none mt-4",
    isFoldable = false,
    // When true, render only the inner sections (transfer header + fare
    // breakdown + additional services) without the outer card wrapper and
    // without the per-booking total footer. Used by MultiTotalCard so several
    // bookings can live inside a single card with one combined grand total.
    renderAsSection = false,
    // Controls whether this card renders its own total footer. Always shown for
    // the standalone card; suppressed when composed inside MultiTotalCard.
    showTotal = true,
    useBorder = false,
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [showDetail, setShowDetail] = useState(!isFoldable);

    const typo13 = getTypographyClass(currentLanguage, 13);
    const typo14 = getTypographyClass(currentLanguage, 14);

    // Every price shown by this card comes from one breakdown so the fare rows, the
    // additional services and the total always agree — whatever the discount source
    // (campaign / IATA / admin-set) and whichever page passes the booking in.
    const breakdown = getBookingPriceBreakdown(cart);

    // The customer flow and admin edits split the discount across the vehicles; bookings
    // created from a quotation only carry the booking-level one, so the breakdown then
    // gets a single discount row of its own instead of one row per vehicle.
    const vehiclesCarryDiscount = (cart?.bookingInfo || []).some((itm) =>
        isDiscountApplied(itm?.discount)
    );

    const checkIsAppliedPackageByVehicles = () => {
        if (!cart?.package?.vehicles) return false;

        return cart?.bookingInfo?.some((itm) =>
            cart?.package.vehicles.some(
                (v) => v.vehicle === itm.vehicleTypeID
            )
        );
    };

    const formatTimeDuration = (totalMinutes) => {
        const decimalHours = totalMinutes / 60;
        const roundedToHalfHour = Math.ceil(decimalHours * 2) / 2;

        const hoursDisplay = roundedToHalfHour % 1 === 0
            ? `${roundedToHalfHour}`
            : `${roundedToHalfHour}`;
        return `${hoursDisplay}`;
    }

    const getTransferTypeTitle = () => {
        let label = "";

        if (cart?.bookingType === "byTheHour") {
            label = t("componentTotalCard.TypeOfTransfer.byTheHour");
        } else if (cart?.isAirportTransfer) {
            label = t("componentTotalCard.TypeOfTransfer.airportTransfer");
        } else {
            label = t("componentTotalCard.TypeOfTransfer.specifiedDestination");
        }

        return label;
    };

    const getTransferTypeLabel = () => {
        if (cart.bookingType === "byTheHour") {
            return t("componentTotalCard.billedDuration", { billedDuration: formatTimeDuration(cart?.billedDuration) });
        } else {
            // Saved bookings carry `formatted_address`; cart/checkout/confirmation
            // items only carry `label`/`name`. Fall back so the transfer label is
            // populated regardless of which page renders the card.
            const pickup = cart?.pickupLocation;
            const pickupName = pickup?.formatted_address?.split(",")?.[0] || pickup?.label || pickup?.name;
            const dropoff = cart?.destinations?.[cart?.destinations.length - 1];
            const dropoffName = dropoff?.formatted_address?.split(",")?.[0] || dropoff?.label || dropoff?.name;

            return `${pickupName} ⇒ ${dropoffName}`;
        }
    };

    const checkVehicleInPackage = (vehicle) => {
        if (!cart?.package) return false;

        const checkVehicle = cart?.package?.vehicles?.find((v) => v.vehicle === vehicle.vehicleTypeID);
        if (checkVehicle) return true;
        return false;
    };

    const getAmountPerVehicle = (itm) => {
        // Child seats are listed under additional services, so they must stay out of the
        // fare: the customer flow folds `childSeatPrice` into the vehicle `total`, while
        // estimate carts and quotation bookings keep it separate.
        const childSeat = breakdown.childSeatsInItemTotals ? (itm.childSeatPrice ?? 0) : 0;

        if (checkVehicleInPackage(itm) && cart?.destinations.length > 1) {
            return itm.total - itm.tollFare - childSeat;
        } else if (checkVehicleInPackage(itm) && cart?.destinations.length === 1) {
            return itm.total - childSeat;
        } else {
            return itm.total - itm.tollFare - itm.longDistanceSurcharge - itm.travelAreaPrice - childSeat
        }
    }

    /** One "<source> Discount (<rate>%) −<amount>" row of the fare breakdown. */
    const renderDiscountRow = (discount, key) => {
        const labelKeys = getDiscountLabelKeys(discount);
        if (!labelKeys) return null;

        const rate = Number(discount?.rate || 0);

        return (
            <div key={key} className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                <div className="tw:!flex-1 tw:!ps-4">
                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#EA4335]">
                        {rate > 0
                            ? t(`componentTotalCard.${labelKeys.row}`, { discount: rate })
                            : t(`componentTotalCard.${labelKeys.applied}`)}
                    </p>
                </div>
                <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#EA4335]">
                        {`-${Number(discount?.amount || 0).toLocaleString()}`}
                        {t("componentTotalCard.jpy")}
                    </p>
                </div>
            </div>
        );
    };

    /**
     * The struck-through pre-discount transfer fee plus the "… discount applied" note.
     * Class names are passed in whole (never interpolated) so Tailwind still sees them.
     */
    const renderDiscountAppliedNote = (labelClassName, amountClassName) => {
        if (!breakdown.hasDiscount || !breakdown.labelKeys) return null;

        return (
            <div className="tw:!flex tw:!w-full tw:!gap-2">
                <div className="tw:!flex-1">
                    <p className={labelClassName}>
                        {t(`componentTotalCard.${breakdown.labelKeys.applied}`)}
                    </p>
                </div>
                <div className=" tw:!flex tw:!items-center tw:!justify-end">
                    <p className={amountClassName}>
                        {breakdown.transferFeeBeforeDiscount.toLocaleString()}
                        {t("componentTotalCard.jpy")}
                    </p>
                </div>
            </div>
        );
    };

    const getVehicleUsageText = (cart, ind, itm, t) => {

        if (cart?.bookingInfo?.length > 1) {
            const indexPrefix = cart?.bookingInfo?.length > 1 ? `${ind + 1}. ` : "";

            const vehicleName = t(itm.vehicleTypeName.replace(/ /g, "."));

            return `${indexPrefix}${vehicleName}`;
        } else {
            return t("componentTotalCard.charterCost");
        }
    };

    const getMeetAssist = () => {
        const meetAssistElements = [];

        if (cart?.meetAssist[0].meetAssistName !== "none" || cart?.meetAssist[1].meetAssistName !== "none") {
            if (cart?.meetAssist[0].meetAssistName !== "none") {
                meetAssistElements.push(
                    <div key="meetAssist-0" className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                        <div className="tw:!flex-1 tw:!ps-2">
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                {`${t(`componentTotalCard.${cart?.meetAssist[0].meetAssistName}.${cart?.isFromAirport ? "airport" : "station"}.arrival`)}`}
                            </p>
                        </div>
                        <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                {`${cart?.meetAssist[0]?.meetAssistPrice?.toLocaleString()}${t("componentTotalCard.jpy")}`}
                            </p>
                        </div>
                    </div>
                );
            }

            if (cart?.meetAssist[1].meetAssistName !== "none") {
                meetAssistElements.push(
                    <div key="meetAssist-1" className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                        <div className="tw:!flex-1 tw:!ps-2">
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                {`${t(`componentTotalCard.${cart?.meetAssist[1].meetAssistName}.${cart?.isToAirport ? "airport" : "station"}.departure`)}`}
                            </p>
                        </div>
                        <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                {`${cart?.meetAssist[1]?.meetAssistPrice?.toLocaleString()}${t("componentTotalCard.jpy")}`}
                            </p>
                        </div>
                    </div>
                );
            }

        }

        return meetAssistElements;
    }

    const inner = (
            <div className={`${useBorder ? "tw:!border-b tw:!border-b-[#E8E8E8]" : ""}`}>
                <div className="card-body">
                    <p className={`tw:!mb-0 tw:!font-[700] tw:!text-[15px] tw:!text-[#4E4E4E]`}>
                        {getTransferTypeTitle()}
                    </p>
                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[13px] tw:!text-[#4E4E4E]">
                        {getTransferTypeLabel()}
                    </p>
                </div>
                <div className="card-body tw:!bg-[#F9F9F9]">
                    <div className="tw:!mb-3 tw:!mt-0">
                        {showDetail && renderDiscountAppliedNote(
                            "tw:!mb-0 tw:!font-[400] tw:!text-[13px] tw:!text-[#7C7B7B]",
                            "tw:!mb-0 tw:!font-[400] tw:!text-[13px] tw:!text-[#EA4335] tw:!line-through tw:!text-right"
                        )}
                        <div className="tw:!flex tw:!w-full tw:!gap-2">
                            <div className="tw:!flex-1">
                                <p className={`tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[${showDetail ? '#282828' : '#7C7B7B'}]`}>
                                    {t("componentTotalCard.transferFee")}
                                </p>
                            </div>
                            <div className=" tw:!flex tw:!items-center tw:!justify-end">
                                <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#282828] tw:!text-right noto">
                                    <AnimatedNumber value={breakdown.transferFee} />
                                    {t("componentTotalCard.jpy")}
                                </p>
                            </div>
                        </div>
                        {!showDetail && renderDiscountAppliedNote(
                            "tw:!mb-0 tw:!font-[400] tw:!text-[12px] tw:!text-[#A7A7A7]",
                            "tw:!mb-0 tw:!font-[400] tw:!text-[12px] tw:!text-[#EA4335] tw:!line-through tw:!text-right"
                        )}

                        <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out ${showDetail ? "tw:grid-rows-[1fr] tw:opacity-100" : "tw:grid-rows-[0fr] tw:opacity-0"}`}>
                            <div className="tw:overflow-hidden">
                                <hr className="tw:!border-[#E8E8E8]" />
                                {/* Basic Fare */}
                                {cart?.bookingInfo?.map((itm, ind) => {
                                    return (
                                        <div key={ind}>
                                            <div className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                                                <div className="tw:!flex-1 tw:!ps-2">
                                                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                                        {getVehicleUsageText(cart, ind, itm, t)}
                                                    </p>
                                                </div>
                                                <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                                        {getAmountPerVehicle(itm).toLocaleString()}
                                                        {t("componentTotalCard.jpy")}
                                                    </p>
                                                </div>
                                            </div>
                                            {renderDiscountRow(itm.discount, `discount-${ind}`)}
                                            {!checkVehicleInPackage(itm) && itm.longDistanceSurcharge > 0 ? (
                                                <div className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                                                    <div className="tw:!flex-1 tw:!ps-2">
                                                        <a
                                                            href="#"
                                                            className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]"
                                                            data-tip
                                                            data-for="longdis2"
                                                        >
                                                            {t("componentTotalCard.longDistanceSurcharge")}
                                                        </a>
                                                        <ReactTooltip
                                                            id="longdis2"
                                                            backgroundColor="#F6EEDC"
                                                            textColor="#4E4E4E"
                                                            place="top"
                                                            effect="solid"
                                                            multiline={true}
                                                            className="fixed-size-tooltip"
                                                            style={{
                                                                maxWidth: '300px',
                                                                width: '300px',
                                                                fontSize: '14px',
                                                                padding: '12px',
                                                                borderRadius: '8px',
                                                                lineHeight: '1.4'
                                                            }}
                                                        >
                                                            <span
                                                                dangerouslySetInnerHTML={{
                                                                    __html: t(
                                                                        "componentTotalCard.longDistanceSurchargeTooltip"
                                                                    ),
                                                                }}
                                                            ></span>
                                                        </ReactTooltip>
                                                    </div>
                                                    <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                                        <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                                            {itm.longDistanceSurcharge.toLocaleString()}
                                                            {t("componentTotalCard.jpy")}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                ""
                                            )}
                                            {(!checkVehicleInPackage(itm) || (checkVehicleInPackage(itm) && cart?.destinations.length > 1)) && itm.tollFare > 0 ? (
                                                <div className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                                                    <div className="tw:!flex-1 tw:!ps-2">
                                                        <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                                            {t("componentTotalCard.tollFare")}
                                                        </p>
                                                    </div>
                                                    <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                                        <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                                            {itm.tollFare.toLocaleString()}
                                                            {t("componentTotalCard.jpy")}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                ""
                                            )}
                                            {cart?.bookingType === "byTheHour" &&
                                                itm.travelAreaPrice !== 0 && (
                                                    <div className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2">
                                                        <div className="tw:!flex-1 tw:!ps-2">
                                                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                                                {t("componentTotalCard.tollFare")}
                                                            </p>
                                                        </div>
                                                        <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                                                {`${itm.travelAreaPrice.toLocaleString()}${t("componentTotalCard.jpy")}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    );
                                })}
                                {!vehiclesCarryDiscount && renderDiscountRow(breakdown.discount, "discount-booking")}
                            </div>
                        </div>

                    </div>
                    {/* Eases open when the first paid extra (Meet & Assist, child
                        seat) is chosen, instead of popping into the card. */}
                    <Collapse isOpen={breakdown.additionalServiceTotal > 0}>
                        {
                            showDetail && (
                                <hr className="tw:!border-dashed tw:!border-[#E8E8E8] tw:!border-[1px] tw:!bg-[#FFFFFF]" />
                            )
                        }
                        <div className="">
                            <div className="tw:!flex tw:!w-full tw:!gap-2">
                                <div className="tw:!flex-1">
                                    <p className={`tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[${showDetail ? '#282828' : '#7C7B7B'}]`}>
                                        {t("BookingConfirmation.AdditionalServiceFee")}
                                    </p>
                                </div>
                                <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                    <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#282828] tw:!text-right noto">
                                        <AnimatedNumber value={breakdown.additionalServiceTotal} />
                                        {t("componentTotalCard.jpy")}
                                    </p>
                                </div>
                            </div>

                            <div className={`tw:grid tw:transition-all tw:duration-300 tw:ease-in-out ${showDetail ? "tw:grid-rows-[1fr] tw:opacity-100" : "tw:grid-rows-[0fr] tw:opacity-0"}`}>
                                <div className="tw:overflow-hidden">
                                    <hr className="tw:!border-[#E8E8E8]" />
                                    {breakdown.childSeatCount > 0 && (
                                        <div
                                            className="tw:!flex tw:!w-full tw:!mb-2 tw:!gap-2"
                                        >
                                            <div className="tw:!flex-1 tw:!ps-2">
                                                <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B]">
                                                    {t("componentTotalCard.childSeat", { count: breakdown.childSeatCount })}
                                                </p>
                                            </div>

                                            <div className="tw:!flex tw:!flex tw:!items-center tw:!justify-end">
                                                <p className="tw:!mb-0 tw:!font-[400] tw:!text-[14px] tw:!text-[#7C7B7B] tw:!text-right">
                                                    <AnimatedNumber value={breakdown.childSeatCount * CHILD_SEAT_PRICE} />
                                                    {t("componentTotalCard.jpy")}
                                                </p>
                                            </div>
                                        </div>
                                    )}


                                    {
                                        getMeetAssist()
                                    }
                                </div>
                            </div>
                        </div>
                    </Collapse>
                    {isFoldable && (
                        <div className="tw:flex tw:flex-col pt-2">
                            <button
                                type="button"
                                onClick={() => setShowDetail(!showDetail)}
                            >
                                <div
                                    className={`tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:rounded-[5px] tw:bg-[#f1f1f2] tw:px-3 tw:py-2 tw:text-[#7c7b7b] tw:font-[400] tw:text-[13px] ${typo13}`}
                                >
                                    <span>
                                        {showDetail ? t("componentTotalCard.hideDetails") : t("componentTotalCard.showDetails")}
                                    </span>
                                    {showDetail ?
                                        <ShowLess
                                            color="#7C7B7B"
                                            size={14}
                                        /> :
                                        <ShowMore
                                            color="#7C7B7B"
                                            size={14}
                                        />
                                    }
                                </div>
                            </button>
                        </div>
                    )}
                </div>

                {showTotal && (
                <div className="card-body">
                    <div className="tw:!flex tw:!justify-start tw:!items-center">
                        <div className="tw:!flex tw:!justify-start tw:!items-center">
                            <p className="tw:!mb-0 tw:!font-[500] tw:!text-[16px] tw:!text-[#282828]">
                                {t("componentTotalCard.totalPrice")}
                            </p>&nbsp;
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[16px] tw:!text-[#8D8D8D]">
                                {t("componentTotalCard.priceTax")}
                            </p>
                        </div>
                        <div className="col d-flex align-items-end flex-column pe-0 ">
                            {
                                breakdown.hasDiscount && (
                                    <p className="text-end tw:!mb-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#EA4335] tw:!line-through"><AnimatedNumber value={breakdown.priceBeforeDiscount} />{t("componentTotalCard.jpy")}</p>
                                )
                            }
                            <p className="tw:!mb-0 tw:!font-[700] tw:!text-[16px] tw:!text-[#282828]">
                                {/* Counts to the new total when an extra is added or removed. */}
                                <AnimatedNumber value={breakdown.totalPrice} />
                                {t("componentTotalCard.jpy")}
                                {cart?.bookingType === "byTheHour" &&
                                    cart?.bookingInfo[0].travelArea === "undecided" && (
                                        <span style={{ color: "#ED553B", fontSize: "18px" }}>
                                            *
                                        </span>
                                    )}
                            </p>
                        </div>
                    </div>
                </div>
                )}
            </div>
    );

    if (renderAsSection) return inner;

    return (
        <div className={className}>
            <div className="card">{inner}</div>
        </div>
    );
};

export default TotalCard;
